import productXapiFetch from '@common-vue/product-carousel/src/services/api';
import productXapiFetchWithAnalytics from './api';
import { getContextUrlParams } from '../utils/utils';

/**
 * Fetches product data in parallel and validates them until the required number
 * of valid products have been fetched or all product IDs have been tried.
 *
 * @param {Array.<string|number>} ids - An array of product IDs to fetch and validate.
 * @param {number} needed - The number of valid products required.
 * @param {function} [validator=() => true] function to validate product data.
 * @param {boolean} [needAnalytics=false] - Whether to fetch product data with analytics.
 * @param {Object} [urlParams={}] - Additional URL parameters to pass to the product API.
 * @returns {Promise.<Array.<Object>>} Returns a Promise that resolves with an array of validated product data.
 * The length of the array will be less than or equal to the 'needed' parameter.
 */
const fetchProducts = async (
  ids,
  needed,
  validator = () => true,
  needAnalytics = false,
  urlParams = {},
  config,
  enableColorRecommendations,
) => {
  const results = [];
  let index = 0;
  while (results.length < needed && ids && index < ids.length) {
    const nextBatchEndIndex = index + (needed - results.length);
    const contextUrlParam = getContextUrlParams(enableColorRecommendations ? config : {});
    const requestFunc = needAnalytics
      ? productXapiFetchWithAnalytics
      : productXapiFetch;
    const promises = ids
      .slice(index, nextBatchEndIndex)
      .map((id) => requestFunc(id, { ...contextUrlParam, ...urlParams }));
    // eslint-disable-next-line no-await-in-loop
    const settled = await Promise.allSettled(promises);
    const successful = settled
      .filter(({ status, value }) => status === 'fulfilled' && validator(value))
      .map((p) => p.value);
    results.push(...successful);
    const nextIndex = index === 0 ? needed : needed - results.length;
    index += nextIndex;
  }

  return results.slice(0, needed);
};

export default fetchProducts;
